import _debounce from "lodash/debounce";
import { useCallback, useEffect, useState } from "react";
import {
    startFetchingWishlist,
    startWhishlistAnimation,
    stopWhishlistAnimation,
} from "web/redux/ducks/app-header-bar";
import store from "web/redux/store";
import analytics from "web/script/analytics/analytics";
import requester from "web/script/modules/requester";
import userProfiler from "web/script/modules/userprofiler";
import logging from "web/script/utils/logging";
import url from "web/script/utils/url";

/**
 * @type {Map<string, boolean>}
 */
let statusCache = new Map();

/** @typedef {{promise: Promise<boolean>, resolve?: (ret: boolean) => void, reject?: (err: Error) => void, saving: bool}} FetchCacheItem */

/**
 * @type {Map<string, FetchCacheItem>}
 */
let fetchCache = new Map();
/**
 * @type {Set<string>}
 */
let fetchQueue = new Set();

/**
 * @param {string} productID
 * @returns {[FetchCacheItem, boolean]}
 */
function createFetchCacheItem(productID) {
    // Shouldn't happen, but just in case
    if (fetchCache.has(productID)) {
        return fetchCache.get(productID);
    }
    let cachedFetch = {};
    // This cannot happen in the initialiser for `cachedFetch` because object
    //  properties are evaluated before the object is created which results in
    //  the `new Promise` callback being invoked before the reference it needs to
    //  modify is available.
    // This is pretty confusing behaviour and took a while to debug :(
    cachedFetch.promise = new Promise((resolve, reject) => {
        cachedFetch.resolve = resolve;
        cachedFetch.reject = reject;
    });

    fetchCache.set(productID, cachedFetch);

    return cachedFetch;
}

export {
    statusCache as __statusCache,
    fetchCache as __fetchCache,
    fetchQueue as __fetchQueue,
    createFetchCacheItem as __createFetchCacheItem,
};

/**
 * @param {string[]} productIDs
 * @returns {Promise<void>}
 */
async function fetchStatuses(productIDs) {
    /* eslint-disable max-depth */
    let data;
    try {
        const query = url.toURLSearchParams({
            // TODO: There's a limit to the size this request can be, we should
            //        split it up into multiple requests if it's too long
            product_id: productIDs,
        });
        data = await requester.get("/services/pins/", query);
    } catch (err) {
        for (let productID of productIDs) {
            let cachedFetch = fetchCache.get(productID);
            if (!cachedFetch || cachedFetch.saving) {
                continue;
            }
            if (cachedFetch.reject) {
                cachedFetch.reject(err);
            }
            fetchCache.delete(productID);
        }
        throw err;
    }

    for (let productID of Object.keys(data.pin_mapping)) {
        let cachedFetch = fetchCache.get(productID);
        if (cachedFetch && cachedFetch.saving) {
            continue;
        }
        let isSaved = data.pin_mapping[productID];
        statusCache.set(productID, isSaved);
        if (cachedFetch && cachedFetch.resolve) {
            cachedFetch.resolve(isSaved);
        }
        fetchCache.delete(productID);
    }

    for (let productID of productIDs) {
        let cachedFetch = fetchCache.get(productID);
        if (!cachedFetch || cachedFetch.saving) {
            continue;
        }
        if (cachedFetch.reject) {
            cachedFetch.reject(new Error(`Server response did not include product "${productID}!`));
        }
        fetchCache.delete(productID);
    }
}

let runQueue = _debounce(() => {
    if (!fetchQueue.size) {
        return;
    }
    fetchStatuses(Array.from(fetchQueue)).catch((err) =>
        logging.error(err, "Failed to run the fetch queue")
    );
    fetchQueue.clear();
}, 50);

/**
 * Asks the save for later system to prefetch a number of product IDs and cache
 *  the results.
 * Useful for situations where you know you're going to render a bunch of
 *  buttons and want to start fetching them all immediately.
 * As with all hooks, you should not dynamically generate the productIDs
 *  parameter or it'll run the effect every single render.
 * @param {string[]} productIDs The products in question
 * @returns {void}
 */
export function useSFLPrefetch(productIDs) {
    useEffect(() => {
        if (!userProfiler.isLoggedIn()) {
            return;
        }

        for (let productID of productIDs) {
            if (statusCache.has(productID) || fetchCache.has(productID)) {
                continue;
            }

            fetchQueue.add(productID);
            let { promise } = createFetchCacheItem(productID);
            // This function doesn't care if any of these promises fail but the
            //  runtime gets upset if you don't catch rejected promises.
            // In some cases, `useSaveForLater` will _also_ catch this error and
            //  report, which could be annoying - but generally the logging
            //  module should catch this and only report it once.
            promise.catch(logging.error);
        }

        runQueue();
    }, [productIDs]);
}

async function actualToggleSave(productID) {
    let response = await requester("/services/pins/", {
        method: "POST",
        body: { product_id: productID },
    });

    fetchCache.delete(productID);

    if (response.status === 204) {
        statusCache.set(productID, false);
        store.dispatch(startFetchingWishlist());
        return false;
    } else if (response.status === 201) {
        statusCache.set(productID, true);
        store.dispatch(startWhishlistAnimation());
        return true;
    }

    throw new Error(`Could not save/un-save product with id: ${productID}`);
}

/**
 * @param {string} productID
 * @returns {Promise<boolean>}
 */
function toggleSave(productID) {
    let cachedFetch = fetchCache.get(productID) || {};

    // If we're already saving this pin, don't do it again
    // Otherwise, we want to take over this fetch mid-flight and change the
    //  result to be the result of the user's action.
    if (cachedFetch.saving) {
        return cachedFetch.promise;
    }

    cachedFetch.saving = true;
    cachedFetch.promise = actualToggleSave(productID);

    let originalResolve = cachedFetch.resolve;
    let originalReject = cachedFetch.reject;
    delete cachedFetch.resolve;
    delete cachedFetch.reject;

    fetchCache.set(productID, cachedFetch);

    return cachedFetch.promise
        .then((result) => {
            if (originalResolve) {
                originalResolve(result);
            }
            return result;
        })
        .catch((err) => {
            if (originalReject) {
                originalReject(err);
            }
            throw err;
        });
}

async function getStatus(productID, initialState = null) {
    if (fetchCache.has(productID)) {
        return await fetchCache.get(productID).promise;
    }
    let cachedStatus = statusCache.get(productID);
    if (cachedStatus != null) {
        return cachedStatus;
    } else if (initialState != null) {
        statusCache.set(productID, initialState);
        return initialState;
    }

    let cachedFetch = createFetchCacheItem(productID);

    fetchQueue.add(productID);
    runQueue();

    return await cachedFetch.promise;
}

const eventLabels = {
    feed: "product_card",
    pdp: "product_page",
    oos: "stock_alert",
};

/**
 * Get the right analytics event label for this pagesource
 * @param {keyof eventLabels} pageSource
 * @returns {string}
 */
export function getAnalyticsEventLabel(pageSource) {
    return eventLabels[pageSource];
}

/**
 * A hook for creating a save for later button.
 * @param {string} productID
 * @param {keyof eventLabels} pageSource
 * @param {boolean | null} initialState
 * @returns {[boolean | null, boolean, () => Promise<boolean>, boolean]}
 */
export function useSaveForLater(productID, pageSource, initialState = null) {
    const [isSaved, setIsSaved] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Enforce productId is always a string.
    productID = productID ? productID.toString() : productID;

    useEffect(() => {
        if (!userProfiler.isLoggedIn() || !productID) {
            return;
        }
        setIsSaved(null);
        setLoading(true);
        getStatus(productID, initialState)
            .then(setIsSaved)
            .catch((err) =>
                logging.error(err, "Failed to get initial save state for product %s:", productID)
            )
            .then(() => setLoading(false));
    }, [productID, initialState]);

    let toggleState = useCallback(async () => {
        if (!userProfiler.isLoggedIn() || !productID) {
            throw new Error("Cannot toggle save state!");
        }

        const eventLabel = getAnalyticsEventLabel(pageSource);
        let newState;
        setLoading(true);
        try {
            // Transitioning from unsaved to saved.
            setIsSaving(!isSaved);
            newState = await toggleSave(productID);
            setIsSaved(newState);
            setIsSaving(false);
            if (isSaved) {
                store.dispatch(stopWhishlistAnimation());
            }
        } catch (err) {
            // Still want to log the intent, but can't work out from response if
            //  it was an add or remove - get this data from the cache instead
            // (this won't be accurate if the page has not been refreshed since
            //  last add/remove, but is the best we can do here).
            newState = !statusCache.get(productID);
            // TODO: Tell the user? Trigger another fetch to see if it actually
            //        went through or not? Does this happen enough to care?
            logging.error(err, "Could not toggle save state for product %s:");
        }
        setLoading(false);

        if (newState) {
            analytics.event("save_for_later", "add_item", eventLabel);
        } else {
            analytics.event("save_for_later", "remove_item", eventLabel);
        }

        return undefined;
    }, [productID, pageSource, isSaved]);

    return [isSaved, loading, toggleState, isSaving];
}

/**
 * A hook finding out if a product is saved for later
 * TODO: This will not update if the user saves a product.
 * @param {string} productID
 * @param {boolean | null} initialState
 * @returns {[boolean | null, boolean]}
 */
export function useGetSaveForLaterStatus(productID = null, initialState = null) {
    const [isSaved, setIsSaved] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!userProfiler.isLoggedIn() || !productID) {
            return;
        }
        setIsSaved(null);
        setLoading(true);
        getStatus(productID, initialState)
            .then(setIsSaved)
            .catch((err) =>
                logging.error(err, "Failed to get initial save state for product %s:", productID)
            )
            .then(() => setLoading(false));
    }, [productID, initialState]);

    return [isSaved, loading];
}
